import { useState } from 'react'
import { useAppStore } from '@/store/useAppStore.ts'
import type { SavedBatteryProfile, SavedLoadProfile } from '@brt/shared-types'

export function ProfileControls() {
  const batteryProfiles = useAppStore((s) => s.savedBatteryProfiles)
  const loadProfiles = useAppStore((s) => s.savedLoadProfiles)
  const saveBatteryProfile = useAppStore((s) => s.saveBatteryProfile)
  const deleteBatteryProfile = useAppStore((s) => s.deleteBatteryProfile)
  const saveLoadProfile = useAppStore((s) => s.saveLoadProfile)
  const deleteLoadProfile = useAppStore((s) => s.deleteLoadProfile)
  const battery = useAppStore((s) => s.battery)
  const load = useAppStore((s) => s.load)
  const computeAll = useAppStore((s) => s.computeAll)

  const [activeInput, setActiveInput] = useState<'battery' | 'load' | null>(null)
  const [profileName, setProfileName] = useState('')

  const handleLoadBatteryProfile = (profile: SavedBatteryProfile) => {
    useAppStore.setState({
      battery: {
        ...battery,
        chemistry: profile.chemistry,
        capacityValue: profile.capacityValue,
        capacityUnit: profile.capacityUnit,
        voltage: profile.voltage,
        dod: profile.dod,
        stateOfCharge: profile.stateOfCharge,
        stateOfHealth: profile.stateOfHealth,
      },
    })
    computeAll()
  }

  const handleLoadLoadProfile = (profile: SavedLoadProfile) => {
    useAppStore.setState({
      load: { ...load, loads: profile.loads.map((l) => ({ ...l })) },
    })
    computeAll()
  }

  const confirmBatterySave = () => {
    const name = profileName.trim()
    if (!name) return
    const profile: SavedBatteryProfile = {
      id: `bp-${Date.now()}`,
      name,
      chemistry: battery.chemistry,
      capacityValue: battery.capacityValue,
      capacityUnit: battery.capacityUnit,
      voltage: battery.voltage,
      dod: battery.dod,
      stateOfCharge: battery.stateOfCharge,
      stateOfHealth: battery.stateOfHealth,
    }
    saveBatteryProfile(profile)
    setProfileName('')
    setActiveInput(null)
  }

  const confirmLoadSave = () => {
    const name = profileName.trim()
    if (!name) return
    const now = new Date().toISOString()
    const profile: SavedLoadProfile = {
      id: `lp-${Date.now()}`,
      name,
      loads: load.loads.map((l) => ({ ...l })),
      createdAt: now,
      updatedAt: now,
      usageCount: 0,
      tags: [],
      isFavorite: false,
    }
    saveLoadProfile(profile)
    setProfileName('')
    setActiveInput(null)
  }

  const cancelInput = () => {
    setProfileName('')
    setActiveInput(null)
  }

  const handleKeyDown = (e: React.KeyboardEvent, type: 'battery' | 'load') => {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (type === 'battery') confirmBatterySave()
      else confirmLoadSave()
    } else if (e.key === 'Escape') {
      cancelInput()
    }
  }

  return (
    <div className="profile-controls">
      <div className="profile-controls__group">
        {activeInput === 'battery' ? (
          <div className="profile-controls__inline-input">
            <input
              type="text"
              value={profileName}
              onChange={(e) => setProfileName(e.target.value)}
              onKeyDown={(e) => handleKeyDown(e, 'battery')}
              placeholder="Profile name…"
              className="profile-controls__name-input"
              autoFocus
              aria-label="Battery profile name"
            />
            <button type="button" className="profile-controls__btn profile-controls__btn--save" onClick={confirmBatterySave} disabled={!profileName.trim()}>
              Save
            </button>
            <button type="button" className="profile-controls__btn" onClick={cancelInput} aria-label="Cancel">
              Cancel
            </button>
          </div>
        ) : (
          <button type="button" className="profile-controls__btn" onClick={() => setActiveInput('battery')}>
            Save Battery
          </button>
        )}
        {batteryProfiles.length > 0 && (
          <details className="profile-controls__dropdown">
            <summary>Load Battery ({batteryProfiles.length})</summary>
            <ul className="profile-controls__list">
              {batteryProfiles.map((p) => (
                <li key={p.id} className="profile-controls__item">
                  <span
                    role="button"
                    tabIndex={0}
                    className="profile-controls__name"
                    onClick={() => handleLoadBatteryProfile(p)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleLoadBatteryProfile(p) } }}
                  >
                    {p.name}
                  </span>
                  <button type="button" onClick={() => deleteBatteryProfile(p.id)} aria-label={`Delete ${p.name}`}>×</button>
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>
      <div className="profile-controls__group">
        {activeInput === 'load' ? (
          <div className="profile-controls__inline-input">
            <input
              type="text"
              value={profileName}
              onChange={(e) => setProfileName(e.target.value)}
              onKeyDown={(e) => handleKeyDown(e, 'load')}
              placeholder="Profile name…"
              className="profile-controls__name-input"
              autoFocus
              aria-label="Load profile name"
            />
            <button type="button" className="profile-controls__btn profile-controls__btn--save" onClick={confirmLoadSave} disabled={!profileName.trim()}>
              Save
            </button>
            <button type="button" className="profile-controls__btn" onClick={cancelInput} aria-label="Cancel">
              Cancel
            </button>
          </div>
        ) : (
          <button type="button" className="profile-controls__btn" onClick={() => setActiveInput('load')}>
            Save Load
          </button>
        )}
        {loadProfiles.length > 0 && (
          <details className="profile-controls__dropdown">
            <summary>Load Load ({loadProfiles.length})</summary>
            <ul className="profile-controls__list">
              {loadProfiles.map((p) => (
                <li key={p.id} className="profile-controls__item">
                  <span
                    role="button"
                    tabIndex={0}
                    className="profile-controls__name"
                    onClick={() => handleLoadLoadProfile(p)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleLoadLoadProfile(p) } }}
                  >
                    {p.name}
                  </span>
                  <button type="button" onClick={() => deleteLoadProfile(p.id)} aria-label={`Delete ${p.name}`}>×</button>
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>
    </div>
  )
}
